// apps/account-portal/src/components/SystemCard.tsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SystemType } from '@redpanda/types';
import { useTranslation } from 'react-i18next';
import { getCookie } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { identityApi } from '../services/api';
import Card from './Card';
import type { SystemIcon } from '../constants/systemDefinitions';

interface SystemCardProps {
  system: SystemType;
  isSubscribed: boolean;
  subscriptionStatus?: string;
  subscriptionEndDate?: Date;
  planName?: string;
  planId?: string;
  icon: SystemIcon;
  features: string[];
  /** 訂閱資料第一次載入中：只有方案那一行顯示佔位，其他固定文字照常顯示 */
  planLoading?: boolean;
}

const CheckRow: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-start gap-2 text-sm text-[var(--text-secondary)] text-balance">
    <svg width="13" height="13" viewBox="0 0 20 20" className="flex-shrink-0 mt-1">
      <polyline points="4,10 8,14 16,6" fill="none" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    {label}
  </div>
);

const SystemCard: React.FC<SystemCardProps> = ({
  system,
  isSubscribed,
  subscriptionStatus,
  subscriptionEndDate,
  planName,
  planLoading = false,
  planId,
  icon: Icon,
  features,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(UserContext)!;

  const systemName = system.toLowerCase();

  const translatePlanName = (planName?: string) => {
    if (!planName) return t('pages.subscription.plan_free');

    if (planName === '免費版') {
      return t(`systems.${systemName}.plans.free.name`, { ns: 'staticContent' });
    }
    if (planName === '基本版') {
      return t(`systems.${systemName}.plans.basic.name`, { ns: 'staticContent' });
    }
    if (planName === '專業版') {
      return t(`systems.${systemName}.plans.pro.name`, { ns: 'staticContent' });
    }

    return planName;
  };

  const handleGoToSubscription = () => navigate(`/${systemName}`);

  const handleAccessSystem = async () => {
    const cleanUrl = (url: string) => url.replace(/\/api\/?$/, '');

    const systemUrlMap = {
      CRM: cleanUrl(process.env.REACT_APP_CRM_FRONTEND_URL || 'http://127.0.0.1:8000'),
      ACCOUNTING: cleanUrl(process.env.REACT_APP_ACCOUNTING_FRONTEND_URL || 'http://127.0.0.1:8002'),
      PM: cleanUrl(process.env.REACT_APP_PM_FRONTEND_URL || 'http://127.0.0.1:8005'),
      HR: cleanUrl(process.env.REACT_APP_HR_FRONTEND_URL || 'http://127.0.0.1:8004'),
      BOOKING: cleanUrl(process.env.REACT_APP_BOOKING_FRONTEND_URL || 'http://127.0.0.1:8006'),
      QUEUE: cleanUrl(process.env.REACT_APP_QUEUE_FRONTEND_URL || 'http://127.0.0.1:8007'),
    } as const;

    // 六個系統都有網址；系統型別若新增尚未上線的系統，這裡會直接不動作
    const url = systemUrlMap[system];
    if (!url) return;
    // 點擊當下先開新分頁：等 API 回來才 window.open，Safari／Firefox 會當成彈出視窗擋掉
    const newTab = window.open('', '_blank');
    if (newTab) newTab.opener = null;

    // 網址帶一次性代碼（2 分鐘、只能用一次），不再帶真正的 token（見 identity-service authCodeService）
    let target = url;
    if (getCookie('auth_token')) {
      try {
        const { data } = await identityApi.post<{ code: string }>('/auth/code');
        const targetUrl = new URL(url);
        targetUrl.searchParams.set('auth_code', data.code);
        target = targetUrl.toString();
      } catch {
        // 拿不到代碼就直接開：正式環境子網域共用 cookie 仍是登入狀態，否則該系統會導向登入頁
      }
    }

    if (newTab) newTab.location.href = target;
    else window.location.href = target; // 新分頁被擋時在目前分頁開
  };

  const isFreeplan = !isSubscribed || planId?.includes('_free');

  return (
    <Card className="group hover:[box-shadow:var(--card-shadow-hover)] transition-shadow duration-200 flex flex-col h-full">
      {/* 系統標題和圖示（標題單行，不強制換行） */}
      <div className="flex items-center mb-3 gap-2.5">
        <Icon className="h-6 w-6 flex-shrink-0 text-[var(--accent)]" />
        <h3 className="text-base font-semibold text-[var(--text-primary)] leading-snug">
          {t(`systems.${systemName}.name`)}
        </h3>
      </div>

      {/* 方案資訊（僅登入後）：方案與到期日合成一行；免費版不顯示「永久／啟用中」，只標示試用這類需要注意的狀態 */}
      {isAuthenticated && (
        <p className="mb-5 text-sm text-[var(--text-secondary)]">
          {planLoading ? (
            <span className="inline-block h-4 w-24 rounded bg-[var(--page-bg)] align-middle animate-pulse motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <span className="font-medium text-[var(--accent)]">{translatePlanName(planName)}</span>
          )}
          {!isFreeplan && subscriptionEndDate && (
            <span> · {t('subscription.expires_on')} {subscriptionEndDate.toLocaleDateString()}</span>
          )}
          {!isFreeplan && subscriptionStatus === 'TRIAL' && <span> · {t('subscription.status.trial')}</span>}
        </p>
      )}

      {/* 功能重點（最多 3 點）：登入後也保留，多數使用者不會每個系統都用過；flex-1 把按鈕推到卡片底部 */}
      <div className={`flex-1 ${isAuthenticated ? 'mb-6' : ''}`}>
        <div className="flex flex-col gap-2">
          {features.slice(0, 3).map((feature, i) => (
            <CheckRow key={i} label={feature} />
          ))}
        </div>
      </div>

      {/* 未登入：只有「查看方案」（方案與價格頁公開，選方案時才要求登入）；原本訪客沒有任何按鈕，價格頁找不到入口 */}
      {!isAuthenticated && (
        <button
          onClick={handleGoToSubscription}
          className="mt-6 px-4 py-2.5 border border-[var(--border-c)] text-[var(--text-primary)] rounded-md hover:bg-[var(--page-bg)] transition-colors duration-150 text-sm"
        >
          {t('common.navigation.viewPlans')}
        </button>
      )}

      {/* 操作按鈕（登入後）；cursor-pointer：滑過按鈕間的空隙時游標不切回箭頭（Windows 快速切換會畫出半截游標） */}
      {isAuthenticated && (
        <div className="flex gap-2 cursor-pointer">
          {(isSubscribed && subscriptionStatus === 'ACTIVE') || isFreeplan ? (
            <>
              <button
                onClick={handleAccessSystem}
                className="flex-1 bg-[var(--accent-fill)] text-[var(--accent-on)] hover:opacity-90 font-semibold py-2.5 px-4 rounded-md transition-opacity duration-150 text-sm"
              >
                {t('systems.access_system')}
              </button>
              <button
                onClick={handleGoToSubscription}
                className="px-4 py-2.5 border border-[var(--border-c)] text-[var(--text-secondary)] rounded-md hover:bg-[var(--page-bg)] transition-colors duration-150 text-sm"
              >
                {t('subscription.manage')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleGoToSubscription}
                className="flex-1 bg-[var(--accent-fill)] text-[var(--accent-on)] hover:opacity-90 font-semibold py-2.5 px-4 rounded-md transition-opacity duration-150 text-sm"
              >
                {isSubscribed ? t('subscription.upgrade') : t('subscription.subscribe')}
              </button>
              {isSubscribed && (
                <button
                  onClick={handleGoToSubscription}
                  className="px-4 py-2.5 border border-[var(--border-c)] text-[var(--text-secondary)] rounded-md hover:bg-[var(--page-bg)] transition-colors duration-150 text-sm"
                >
                  {t('subscription.manage')}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default SystemCard;
